import { Dialog } from '@medely/web-components';
import { useCurrentUser } from '@mp/hooks';
import React, { useEffect, useState } from 'react';
import { ExperienceSummaryResumeModalProps } from './interface';
import { Box, Button, FileField, Form, HStack, SubmitButton, Text, yup } from '@medely/ui-kit';
import useResumeSubmitMutation from './useResumeMutations';
import { yupResumeFileValidator, validateResumeFile } from '@mp/utils/fileValidation';

export const parseFileString = (fileString: string) => {
  const lastSlashIdx = fileString.lastIndexOf('/');
  const fileNameAfterSlash = fileString.slice(lastSlashIdx + 1);
  const questionMarkIdx = fileNameAfterSlash.indexOf('?') || undefined;
  return fileNameAfterSlash.slice(0, questionMarkIdx);
};

export const ExperienceSummaryResumeModal = ({
  open,
  onClose,
}: ExperienceSummaryResumeModalProps) => {
  const { currentUser } = useCurrentUser();
  const { submitResume, isLoading } = useResumeSubmitMutation(onClose);
  const [fileErrorMessage, setFileErrorMessage] = useState<string | null>(null);
  const [hasResumeChanged, setHasResumeChanged] = useState(false);

  const resume = currentUser?.professional?.resume;

  const handleContinue = async (values: { resume: File | undefined }) => {
    submitResume(values);
  };

  const handleFileChange = (value: File | string | null) => {
    if (value instanceof File) {
      const validation = validateResumeFile(value);
      if (!validation.isValid) {
        setFileErrorMessage(validation.error || 'Invalid file');
        setHasResumeChanged(true);
        return value;
      } else {
        setFileErrorMessage(null);
        setHasResumeChanged(true);
      }
    } else {
      setFileErrorMessage(null);
      setHasResumeChanged(value !== resume);
    }
    return value;
  };

  useEffect(() => {
    return () => {
      setHasResumeChanged(false);
    };
  }, [resume]);

  return (
    <Dialog
      open={open}
      disableRestoreFocus
      title={!!resume ? `Replace resume` : 'Upload resume'}
      content={
        <>
          <Form
            defaultValues={{ resume }}
            handleSubmit={handleContinue}
            schema={yup.object({
              resume: yup
                .mixed()
                .test('resume-validation', (value) => yupResumeFileValidator(value, true)),
            })}
          >
            <Box my={2}>
              <FileField
                acceptedFileTypes={['.doc', '.docx', '.pdf']}
                name="resume"
                label="Upload your resume"
                parseValue={(value) => {
                  if (typeof value === 'string') {
                    return parseFileString(value);
                  }
                  return handleFileChange(value);
                }}
              />
              {fileErrorMessage && (
                <Box mt={1}>
                  <Text size="s" color="state.error.secondary">
                    {fileErrorMessage}
                  </Text>
                </Box>
              )}
            </Box>
            <HStack>
              <Box mr={0.5} width="100%">
                <Button variant="outlined" onClick={onClose} fullWidth>
                  Back
                </Button>
              </Box>
              <Box ml={0.5} width="100%">
                <SubmitButton
                  fullWidth
                  disabled={isLoading || !!fileErrorMessage || !hasResumeChanged}
                >
                  Save
                </SubmitButton>
              </Box>
            </HStack>
          </Form>
        </>
      }
    />
  );
};
